/**
 * AacAccessUnit — the one door for AAC that WebCodecs produced on its way into
 * an MP4 track.
 *
 * The `mp4a` sample entry promises two things: RAW access units in the samples
 * and an AudioSpecificConfig in `esds`. Chromium 109–111 on Windows broke both
 * — M109/M110 on Windows 8+ and M111 on every Windows configured the Media
 * Foundation AAC encoder with MF_MT_AAC_PAYLOAD_TYPE=1, so every
 * `AudioEncoder` chunk arrived ADTS-framed (7-byte header, 0xFFF sync), and
 * none of those builds read MF_MT_USER_DATA, so `decoderConfig.description`
 * was never set (media/gpu/windows/mf_audio_encoder.cc at those tags; the RAW
 * default via `AacOutputFormat` and the description arrived in M112).
 *
 * Copied verbatim into the container, such a file plays WITH sound in every
 * FFmpeg-based decoder — Chrome, VLC, Android players, our own FFmpeg lanes,
 * ffprobe — because they resync on the ADTS header, and with NO sound in every
 * Media Foundation player — Windows Media Player, «Кино и ТВ», Media Player:
 * the Source Reader returns zero PCM samples and no error, so the picture
 * plays and the soundtrack is simply absent. Report #6990 (12.09.2026,
 * /slideshow-maker, Chrome 109 on Windows: «на компьютере и ноутбуке
 * воспроизводится без звука, на смартфоне идёт со звуком»). Measured on this
 * class with the real primitive in real Chrome: the same 4-second tone decodes
 * through Media Foundation to 4.02 s at −13.5 dBFS as raw access units and to
 * 0 samples with the ADTS header left in (tests/playwright/aac-access-unit-
 * strips-adts.mjs). RU analytics, 90 days: 8 769 Windows sessions on those
 * builds, 51 WebCodecs MP4 exports on /slideshow-maker alone.
 *
 * The SECOND promise broke the other way round (report #7211, 15.09.2026,
 * /add-music-to-video, iPhone, Safari 26.6.1: «Не скачивается файл» — the
 * video was a /slideshow-maker export made on the same phone). WebKit's
 * AudioEncoder DOES set decoderConfig.description — but it is the AudioToolbox
 * "magic cookie" verbatim (AudioEncoderCocoa.cpp,
 * generateDecoderDescriptionFromSample → CMAudioFormatDescriptionGetMagicCookie),
 * and for AAC that cookie is the MPEG-4 descriptor chain ES_Descriptor(0x03) →
 * DecoderConfigDescriptor(0x04) → DecoderSpecificInfo(0x05) → the
 * AudioSpecificConfig: the esds PAYLOAD, not the bare ASC the WebCodecs
 * registry asks for. Chromium's own AudioToolbox encoder unwraps exactly this
 * (audio_toolbox_audio_encoder.cc: «The magic cookie is an ISO-BMFF ESDS box»,
 * mp4::ESDescriptor::Parse → decoder_specific_info); WebKit does not. Written
 * into esds as the DecoderSpecificInfo it nests a descriptor inside a
 * descriptor, and the first five bits of 0x03 read as audioObjectType 0 — so
 * every FFmpeg-based decoder (our FFmpeg lanes, Chrome on the desktop, VLC,
 * Telegram) refuses the track with «Audio object type 0 is not implemented»
 * and the export dies with exit code 1, while Apple's decoders, which take a
 * cookie in either shape, play the same file — the mirror image of #6990.
 *
 * Why it surfaced on 12.09 and not before: the door checked that a description
 * EXISTS, never that it IS an AudioSpecificConfig. Until 12.09 the callers'
 * per-chunk «synthesize when missing» fired on every chunk AFTER the first
 * (those carry no meta), and mp4-muxer Object.assign-merges decoderConfig from
 * any chunk that has one — so the synthesized ASC silently overwrote WebKit's
 * cookie one chunk later. The door hands later chunks their (absent) meta, the
 * cookie stayed, and «Audio object type 0» appeared in the logs the same day
 * (RU, 90 days: 0 before 12.09, 13 sessions in the next four days).
 *
 * Front door: `AacAccessUnit.encoder({output, error}, {slug})` — a real
 * AudioEncoder whose output callback receives spec-shaped chunks: raw access
 * units, and a first-chunk `decoderConfig.description` that is ALWAYS present
 * AND READS as an AudioSpecificConfig: the encoder's own when it is one, else
 * the ASC unwrapped from the descriptor chain the encoder handed out, else
 * derived from the ADTS header just stripped, else from the configured
 * sampleRate/channels. Opus, and AAC the caller explicitly asked for as ADTS
 * (`aac: {format: 'adts'}`), pass through untouched. `normalize(chunk, meta,
 * cfg)` is the same door for a chunk in hand. Nothing here touches the bytes of
 * a frame that is already raw. Both rewrites are reported once per encoder:
 * `aac_adts_stripped` and `aac_desc_unwrapped` (with `src`: esds | synth).
 *
 * `repairMp4(fileOrBytes)` applies the same reading to a FINISHED file: every
 * mp4a sample entry whose esds DecoderSpecificInfo is not an ASC gets one
 * written in place (ASC first, zero-filled to the old length — decoders read
 * the fields they need and never the tail, and box sizes / chunk offsets do not
 * move). The FFmpeg processor runs it on every mp4-family input, so the MP4s
 * WebKit users made between 12.09 and this fix keep working in our tools.
 *
 * Guards: tests/js/aac-access-unit.test.mjs (the door itself, on fake encoders
 * shaped like Chrome 109 and like Safari 26; repairMp4 on a synthesized moov),
 * AacAccessUnitsGoThroughThePrimitiveTest (no AAC→MP4 site may construct a
 * bare AudioEncoder; the processor heals through the door), and the Playwright
 * stands (real primitive, real Chrome, Chrome-109 emulation via
 * `aac:{format:'adts'}` with Media Foundation as the oracle; Safari-26
 * emulation via a cookie-shaped description with FFmpeg as the oracle).
 */
(function (root) {
    'use strict';
    if (root.AacAccessUnit) return;

    // ISO/IEC 14496-3, samplingFrequencyIndex → Hz (index 13/14 reserved, 15 = explicit).
    var SAMPLE_RATES = [96000, 88200, 64000, 48000, 44100, 32000, 24000, 22050, 16000, 12000, 11025, 8000, 7350];
    // channelConfiguration → channels (0 = a program config element decides; 7 = 7.1 = eight).
    var ASC_CHANNELS = [0, 1, 2, 3, 4, 5, 6, 8];

    /* Parse an ADTS fixed+variable header at `off`. Returns null unless the
       bytes are a well-formed header: 12 sync bits, layer 0, a sampling index
       the table knows and a frame length that fits the buffer. */
    function parseAdts(b, off) {
        off = off || 0;
        if (!b || b.length - off < 7) return null;
        if (b[off] !== 0xFF || (b[off + 1] & 0xF6) !== 0xF0) return null;   // sync 0xFFF, layer 00
        var crc = (b[off + 1] & 0x01) === 0;                                   // protection_absent == 0 → CRC present
        var profile = (b[off + 2] >> 6) & 0x03;                                // 0 = Main, 1 = LC, 2 = SSR, 3 = LTP
        var sri = (b[off + 2] >> 2) & 0x0F;
        var cc = ((b[off + 2] & 0x01) << 2) | ((b[off + 3] >> 6) & 0x03);
        var frameLen = ((b[off + 3] & 0x03) << 11) | (b[off + 4] << 3) | ((b[off + 5] >> 5) & 0x07);
        var headerLen = crc ? 9 : 7;
        if (sri >= SAMPLE_RATES.length) return null;
        if (frameLen < headerLen || off + frameLen > b.length) return null;
        return { headerLen: headerLen, frameLen: frameLen, crc: crc, aot: profile + 1,
                 sri: sri, sampleRate: SAMPLE_RATES[sri], channels: cc };
    }

    /* The whole buffer is one ADTS frame, or a run of them that covers it
       exactly. Anything else — a raw access unit, a truncated header, a frame
       whose declared length disagrees with the buffer — is left alone. */
    function adtsFrames(b) {
        var frames = [], off = 0;
        while (off < b.length) {
            var h = parseAdts(b, off);
            if (!h) return null;
            frames.push({ off: off, h: h });
            off += h.frameLen;
        }
        return frames.length ? frames : null;
    }

    function isAdts(bytes) { return !!adtsFrames(toU8(bytes)); }

    /* Strip every ADTS header; returns { data, header } where header is the
       first frame's parse (null when nothing was stripped and data === input). */
    function strip(bytes) {
        var b = toU8(bytes);
        var frames = adtsFrames(b);
        if (!frames) return { data: b, header: null };
        var total = 0, i;
        for (i = 0; i < frames.length; i++) total += frames[i].h.frameLen - frames[i].h.headerLen;
        var out = new Uint8Array(total), p = 0;
        for (i = 0; i < frames.length; i++) {
            var f = frames[i];
            out.set(b.subarray(f.off + f.h.headerLen, f.off + f.h.frameLen), p);
            p += f.h.frameLen - f.h.headerLen;
        }
        return { data: out, header: frames[0].h };
    }

    /* AudioSpecificConfig — audioObjectType(5) samplingFrequencyIndex(4)
       channelConfiguration(4), padded to two bytes. */
    function asc(opts) {
        opts = opts || {};
        var sri = opts.sri;
        if (!(sri >= 0)) { sri = SAMPLE_RATES.indexOf(+opts.sampleRate); if (sri < 0) sri = 4; }   // 44100 when unknown
        var aot = opts.aot > 0 ? opts.aot : 2;                                                     // AAC-LC
        var cc = Math.max(1, Math.min(7, opts.channels || 2));
        return new Uint8Array([(aot << 3) | (sri >> 1), ((sri & 1) << 7) | (cc << 3)]);
    }

    /**
     * readAsc(bytes) → { aot, sri, sampleRate, cc, channels } or null.
     * The leading fields of an AudioSpecificConfig (ISO/IEC 14496-3 §1.6.2.1),
     * read the way a decoder reads them. null means «these bytes are not one»:
     * fewer than two bytes; audioObjectType 0 (AOT_NULL — no stream has it, and
     * every MPEG-4 descriptor tag and every 32-bit box size starts with it,
     * which is exactly how a cookie or a whole esds box shows up here); a
     * reserved samplingFrequencyIndex (13, 14). It does NOT judge whether the
     * object type is one a particular decoder implements — that is the
     * decoder's call, and HE-AAC (5), PS (29), ELD (39) are all legitimate.
     */
    function readAsc(bytes) {
        var b = toU8(bytes);
        if (b.length < 2) return null;
        var bit = 0;
        function take(n) {
            var v = 0;
            for (var i = 0; i < n; i++) {
                var byteI = bit >> 3;
                if (byteI >= b.length) return -1;
                v = (v * 2) + ((b[byteI] >> (7 - (bit & 7))) & 1);
                bit++;
            }
            return v;
        }
        var aot = take(5);
        if (aot === 31) { var ext = take(6); if (ext < 0) return null; aot = 32 + ext; }
        if (aot <= 0) return null;
        var sri = take(4);
        if (sri < 0) return null;
        var rate;
        if (sri === 15) { rate = take(24); if (rate <= 0) return null; }
        else { if (sri >= SAMPLE_RATES.length) return null; rate = SAMPLE_RATES[sri]; }
        var cc = take(4);
        if (cc < 0) return null;
        return { aot: aot, sri: sri, sampleRate: rate, cc: cc, channels: cc < ASC_CHANNELS.length ? ASC_CHANNELS[cc] : 0 };
    }

    /* MPEG-4 descriptor length (ISO/IEC 14496-1 §8.3.3): 1–4 bytes, 7 bits
       each, high bit = «one more byte». AudioToolbox always spends four
       (0x80 0x80 0x80 NN); FFmpeg-made files spend one. */
    function descLen(b, i) {
        var len = 0, n = 0, c;
        do {
            if (i + n >= b.length) return null;
            c = b[i + n]; len = (len * 128) + (c & 0x7f); n++;
        } while ((c & 0x80) && n < 4);
        return { len: len, read: n };
    }

    /**
     * findDsi(bytes) → { at, len } or null: where the DecoderSpecificInfo
     * payload (= the AudioSpecificConfig) sits inside an MPEG-4 descriptor
     * chain. Accepts the chain in the three shapes it travels in: the bare
     * ES_Descriptor (what AudioToolbox calls the magic cookie), the same behind
     * the 4-byte version/flags of an esds FullBox payload, or a whole `esds`
     * box (size, type, version/flags). ES_Descriptor: tag 0x03, length, ES_ID
     * (2), flags (streamDependence 0x80 → +2, URL 0x40 → +1+len, OCR 0x20 → +2);
     * DecoderConfigDescriptor: tag 0x04, length, then 13 fixed bytes
     * (objectTypeIndication, streamType, bufferSizeDB×3, maxBitrate×4,
     * avgBitrate×4); DecoderSpecificInfo: tag 0x05, length, payload.
     */
    function findDsi(bytes) {
        var b = toU8(bytes);
        var starts = [0];
        if (b.length >= 12 && b[4] === 0x65 && b[5] === 0x73 && b[6] === 0x64 && b[7] === 0x73) starts.push(12);   // 'esds' box
        if (b.length >= 5 && b[0] === 0 && b[1] === 0 && b[2] === 0 && b[3] === 0) starts.push(4);                 // version/flags
        for (var s = 0; s < starts.length; s++) {
            var p = starts[s], L;
            if (p >= b.length || b[p] !== 0x03) continue;
            L = descLen(b, ++p); if (!L) continue; p += L.read;
            p += 2;                                              // ES_ID
            if (p >= b.length) continue;
            var flags = b[p++];
            if (flags & 0x80) p += 2;
            if (flags & 0x40) { if (p >= b.length) continue; p += 1 + b[p]; }
            if (flags & 0x20) p += 2;
            if (p >= b.length || b[p] !== 0x04) continue;
            L = descLen(b, ++p); if (!L) continue; p += L.read;
            p += 13;
            if (p >= b.length || b[p] !== 0x05) continue;
            L = descLen(b, ++p); if (!L) continue; p += L.read;
            if (L.len >= 2 && p + L.len <= b.length) return { at: p, len: L.len };
        }
        return null;
    }

    /**
     * sanitizeDescription(desc, cfg) → { description, src, len, b0 }
     *   src 'own'   — desc reads as an AudioSpecificConfig; returned as the SAME object
     *   src 'esds'  — desc was a descriptor chain; the ASC inside it is returned
     *   src 'synth' — neither; an AAC-LC ASC from cfg.sampleRate / cfg.numberOfChannels
     * cfg may also carry sri/aot/channels from a stripped ADTS header.
     */
    function sanitizeDescription(desc, cfg) {
        cfg = cfg || {};
        var b = toU8(desc);
        if (readAsc(b)) return { description: desc, src: 'own', len: b.length, b0: b.length ? b[0] : -1 };
        var dsi = findDsi(b);
        if (dsi) {
            var inner = b.subarray(dsi.at, dsi.at + dsi.len);
            if (readAsc(inner)) return { description: new Uint8Array(inner), src: 'esds', len: b.length, b0: b[0] };
        }
        return { description: asc({ sri: cfg.sri, aot: cfg.aot, sampleRate: cfg.sampleRate, channels: cfg.channels || cfg.numberOfChannels }),
                 src: 'synth', len: b.length, b0: b.length ? b[0] : -1 };
    }

    function toU8(x) {
        if (x instanceof Uint8Array) return x;
        if (x instanceof ArrayBuffer) return new Uint8Array(x);
        if (x && x.buffer instanceof ArrayBuffer) return new Uint8Array(x.buffer, x.byteOffset || 0, x.byteLength);
        if (x && typeof x.copyTo === 'function') { var u = new Uint8Array(x.byteLength); x.copyTo(u); return u; }
        return new Uint8Array(0);
    }

    function isAacCodec(codec) { return /^mp4a(\.|$)/i.test(String(codec || '')); }

    function hasDescription(meta) {
        var d = meta && meta.decoderConfig && meta.decoderConfig.description;
        return !!(d && (d.byteLength > 0 || (d.buffer && d.buffer.byteLength > 0)));
    }

    /**
     * normalize(chunk, meta, cfg) → { chunk, meta, stripped, header, descSrc, descFrom }
     *   chunk — EncodedAudioChunk (or { type, timestamp, duration, data })
     *   meta  — EncodedAudioChunkMetadata the encoder handed over (may be undefined)
     *   cfg   — { codec, sampleRate, numberOfChannels, wantsAdts, first }
     * For an AAC codec, unless the caller asked for ADTS: strips ADTS framing,
     * and on the first chunk (or any chunk carrying a decoderConfig) makes sure
     * `decoderConfig.description` exists AND reads as an AudioSpecificConfig —
     * a description that does not (WebKit's descriptor-chain cookie) is
     * replaced by the ASC found inside it, or synthesized. Non-AAC codecs pass
     * through as-is. descSrc: 'own' | 'esds' | 'synth' | null (no config here);
     * descFrom: { len, b0 } of the description that was replaced, else null.
     */
    function normalize(chunk, meta, cfg) {
        cfg = cfg || {};
        if (!isAacCodec(cfg.codec) || cfg.wantsAdts) return { chunk: chunk, meta: meta, stripped: false, header: null, descSrc: null, descFrom: null };
        var bytes = toU8(chunk && chunk.data !== undefined ? chunk.data : chunk);
        var s = strip(bytes);
        var outChunk = chunk;
        if (s.header) {
            var init = { type: chunk.type || 'key', timestamp: chunk.timestamp || 0, data: s.data };
            if (chunk.duration != null) init.duration = chunk.duration;
            outChunk = (typeof root.EncodedAudioChunk === 'function') ? new root.EncodedAudioChunk(init) : init;
        }
        var outMeta = meta, descSrc = null, descFrom = null;
        var needDesc = (meta && meta.decoderConfig && !hasDescription(meta)) || (cfg.first && !hasDescription(meta));
        if (needDesc) {
            var dc = Object.assign({}, (meta && meta.decoderConfig) || {});
            if (!dc.codec) dc.codec = cfg.codec;
            if (!dc.sampleRate) dc.sampleRate = s.header ? s.header.sampleRate : cfg.sampleRate;
            if (!dc.numberOfChannels) dc.numberOfChannels = s.header ? s.header.channels : cfg.numberOfChannels;
            dc.description = s.header
                ? asc({ sri: s.header.sri, channels: s.header.channels, aot: s.header.aot })
                : asc({ sampleRate: dc.sampleRate, channels: dc.numberOfChannels });
            outMeta = Object.assign({}, meta || {}, { decoderConfig: dc });
            descSrc = 'synth';
        } else if (hasDescription(meta)) {
            var san = sanitizeDescription(meta.decoderConfig.description, {
                sampleRate: meta.decoderConfig.sampleRate || (s.header ? s.header.sampleRate : cfg.sampleRate),
                numberOfChannels: meta.decoderConfig.numberOfChannels || (s.header ? s.header.channels : cfg.numberOfChannels),
                sri: s.header ? s.header.sri : undefined, aot: s.header ? s.header.aot : undefined
            });
            descSrc = san.src;
            if (san.src !== 'own') {
                outMeta = Object.assign({}, meta, { decoderConfig: Object.assign({}, meta.decoderConfig, { description: san.description }) });
                descFrom = { len: san.len, b0: san.b0 };
            }
        }
        return { chunk: outChunk, meta: outMeta, stripped: !!s.header, header: s.header, descSrc: descSrc, descFrom: descFrom };
    }

    function slugOf(st) {
        var slugEl = root.document && root.document.querySelector && root.document.querySelector('meta[name="tool-slug"]');
        return st.slug || root.__TOOL_SLUG || (slugEl && slugEl.content) || 'unknown';
    }

    function report(st, header) {
        try {
            if (typeof root.__ae !== 'function') return;
            root.__ae('feature_use', {
                f: 'aac_adts_stripped', slug: slugOf(st),
                hdr: header.headerLen, sr: header.sampleRate, ch: header.channels, aot: header.aot
            });
        } catch (_) {}
    }

    function reportDesc(st, r) {
        try {
            if (typeof root.__ae !== 'function') return;
            root.__ae('feature_use', {
                f: 'aac_desc_unwrapped', slug: slugOf(st), src: r.descSrc,
                len: r.descFrom.len, b0: r.descFrom.b0 < 0 ? '' : ('0' + r.descFrom.b0.toString(16)).slice(-2)
            });
        } catch (_) {}
    }

    /**
     * encoder(init, opts) → AudioEncoder
     * A native AudioEncoder whose `output` sees normalized chunks. The codec,
     * rates and the caller's own `aac.format` wish are read from configure().
     */
    function encoder(init, opts) {
        if (typeof root.AudioEncoder !== 'function') throw new Error('AudioEncoder unavailable');
        init = init || {};
        var st = { codec: null, sampleRate: 0, numberOfChannels: 0, wantsAdts: false, first: true,
                   stripped: 0, unwrapped: 0, slug: opts && opts.slug };
        var userOutput = init.output;
        var enc = new root.AudioEncoder({
            output: function (chunk, meta) {
                var r = normalize(chunk, meta, st);
                st.first = false;
                if (r.stripped) { st.stripped++; if (st.stripped === 1) report(st, r.header); }
                if (r.descFrom) { st.unwrapped++; if (st.unwrapped === 1) reportDesc(st, r); }
                return userOutput(r.chunk, r.meta);
            },
            error: init.error
        });
        var nativeConfigure = enc.configure;
        enc.configure = function (c) {
            c = c || {};
            st.codec = c.codec; st.sampleRate = c.sampleRate; st.numberOfChannels = c.numberOfChannels;
            st.wantsAdts = !!(c.aac && c.aac.format === 'adts');
            st.first = true;
            return nativeConfigure.call(this, c);
        };
        enc.__aacAccessUnit = st;   // inspectable by tests
        return enc;
    }

    /* ───────────────────────── repairMp4 ───────────────────────── */

    function u32(b, o) { return ((b[o] << 24) >>> 0) + (b[o + 1] << 16) + (b[o + 2] << 8) + b[o + 3]; }
    function str4(b, o) { return String.fromCharCode(b[o], b[o + 1], b[o + 2], b[o + 3]); }
    function u16(b, o) { return (b[o] << 8) | b[o + 1]; }

    /* Walk the boxes of one buffer region, calling fn(type, contentStart, contentEnd). */
    function eachBox(b, start, end, fn) {
        var p = start;
        while (p + 8 <= end) {
            var size = u32(b, p), hdr = 8;
            if (size === 1) { if (p + 16 > end) return; size = u32(b, p + 8) * 4294967296 + u32(b, p + 12); hdr = 16; }
            else if (size === 0) size = end - p;
            if (size < hdr) return;
            if (p + size > end) size = end - p;                  // truncated read — use what we have
            fn(str4(b, p + 4), p + hdr, p + size);
            p += size;
        }
    }

    /**
     * ascSitesInMoov(moov) → [{ at, len, dsi }] — every mp4a sample entry's
     * esds DecoderSpecificInfo inside a moov buffer (`at`/`len` relative to
     * `moov`), with the AudioSampleEntry's own samplerate/channelcount for the
     * synth fallback. QuickTime's `wave` wrapper is looked into like an .m4a.
     */
    function ascSitesInMoov(b) {
        var sites = [];
        function esdsIn(start, end, entry) {
            eachBox(b, start, end, function (ty, cs, ce) {
                if (ty === 'esds') {
                    var d = findDsi(b.subarray(cs, ce));
                    if (d) sites.push({ at: cs + d.at, len: d.len, entryRate: entry.rate, entryChannels: entry.ch });
                } else if (ty === 'wave') {
                    esdsIn(cs, ce, entry);
                }
            });
        }
        function stsd(cs, ce) {
            var n = u32(b, cs + 4), q = cs + 8;
            for (var i = 0; i < n && q + 36 <= ce; i++) {
                var esize = u32(b, q), etype = str4(b, q + 4);
                if (esize < 36 || q + esize > ce) break;
                if (etype === 'mp4a') {
                    var sver = u16(b, q + 16), ch = u16(b, q + 24), rate = u16(b, q + 32);
                    var extra = sver === 1 ? 16 : (sver === 2 ? 36 : 0);
                    esdsIn(q + 36 + extra, q + esize, { rate: rate, ch: ch });
                }
                q += esize;
            }
        }
        (function walk(start, end) {
            eachBox(b, start, end, function (ty, cs, ce) {
                if (ty === 'trak' || ty === 'mdia' || ty === 'minf' || ty === 'stbl') walk(cs, ce);
                else if (ty === 'stsd') stsd(cs, ce);
            });
        })(0, b.length);
        return sites;
    }

    /* Patch every site whose bytes are not an ASC. Returns the list of what was done. */
    function patchMoov(moov) {
        var done = [];
        var sites = ascSitesInMoov(moov);
        for (var i = 0; i < sites.length; i++) {
            var s = sites[i];
            var dsi = moov.subarray(s.at, s.at + s.len);
            if (readAsc(dsi)) continue;
            var was = dsi.length ? ('0' + dsi[0].toString(16)).slice(-2) : '';
            var fresh = null, src = null;
            var nested = findDsi(dsi);
            if (nested) {
                var inner = dsi.subarray(nested.at, nested.at + nested.len);
                if (readAsc(inner)) { fresh = new Uint8Array(inner); src = 'esds'; }
            }
            if (!fresh) { fresh = asc({ sampleRate: s.entryRate, channels: s.entryChannels }); src = 'synth'; }
            if (fresh.length > dsi.length) continue;             // cannot grow in place — leave it to the decoder to refuse honestly
            dsi.fill(0);
            dsi.set(fresh, 0);
            done.push({ at: s.at, len: s.len, src: src, was: was, asc: fresh });
        }
        return done;
    }

    function readRange(file, start, end) {
        var s = Math.max(0, start), e = Math.min(file.size, end);
        if (e <= s) return Promise.resolve(new Uint8Array(0));
        return file.slice(s, e).arrayBuffer().then(function (ab) { return new Uint8Array(ab); });
    }

    var MOOV_MAX = 64 * 1024 * 1024;

    /**
     * repairMp4(input) → Promise<{ changed, result, tracks, reason }>
     *   input   — File/Blob (mp4-family), or a Uint8Array holding the whole file
     *   result  — the same object when nothing needed doing; else a new File
     *             (0..moov slice · patched moov · rest slice — the media bytes
     *             are referenced, not copied) or the same Uint8Array patched in
     *             place
     *   tracks  — one entry per esds rewritten: { at (absolute), len, src, was }
     *   reason  — why nothing was done, when changed is false
     * Never throws: any failure to read or to parse returns the input untouched
     * with a reason — the repair is a rescue, not a gate.
     */
    async function repairMp4(input) {
        var out = { changed: false, result: input, tracks: [], reason: null };
        try {
            if (input instanceof Uint8Array) {
                var moovAt = -1, moovEnd = -1;
                eachBox(input, 0, input.length, function (ty, cs, ce) { if (ty === 'moov' && moovAt < 0) { moovAt = cs; moovEnd = ce; } });
                if (moovAt < 0) { out.reason = 'no_moov'; return out; }
                var doneB = patchMoov(input.subarray(moovAt, moovEnd));
                if (!doneB.length) { out.reason = 'valid'; return out; }
                out.changed = true;
                out.tracks = doneB.map(function (d) { return { at: moovAt + d.at, len: d.len, src: d.src, was: d.was }; });
                return out;
            }
            if (!input || typeof input.slice !== 'function' || !(input.size > 16)) { out.reason = 'not_a_blob'; return out; }
            var pos = 0, guard = 0, mAt = -1, mSize = 0, mHdr = 8;
            while (pos < input.size && guard++ < 512) {
                var head = await readRange(input, pos, pos + 16);
                if (head.length < 8) break;
                var size = u32(head, 0), type = str4(head, 4), hdr = 8;
                if (size === 1) { if (head.length < 16) break; size = u32(head, 8) * 4294967296 + u32(head, 12); hdr = 16; }
                else if (size === 0) size = input.size - pos;
                if (size < hdr) break;
                if (type === 'moov') { mAt = pos; mSize = size; mHdr = hdr; break; }
                pos += size;
            }
            if (mAt < 0) { out.reason = 'no_moov'; return out; }
            if (mSize > MOOV_MAX) { out.reason = 'moov_too_large'; return out; }
            var moov = await readRange(input, mAt + mHdr, mAt + mSize);
            var done = patchMoov(moov);
            if (!done.length) { out.reason = 'valid'; return out; }
            var parts = [input.slice(0, mAt + mHdr), moov, input.slice(mAt + mSize)];
            var name = input.name || 'input.mp4';
            var init = { type: input.type || 'video/mp4' };
            if (input.lastModified) init.lastModified = input.lastModified;
            // Bare globals, not root.*: in a page they are the same, in the unit-test
            // world `root` is a stand-in object and File/Blob live on the Node global.
            out.result = (typeof File === 'function') ? new File(parts, name, init) : new Blob(parts, init);
            out.changed = true;
            out.tracks = done.map(function (d) { return { at: mAt + mHdr + d.at, len: d.len, src: d.src, was: d.was }; });
            return out;
        } catch (e) {
            out.changed = false; out.result = input; out.tracks = [];
            out.reason = 'error:' + String((e && e.message) || e).slice(0, 80);
            return out;
        }
    }

    root.AacAccessUnit = {
        SAMPLE_RATES: SAMPLE_RATES,
        parseAdts: function (bytes, off) { return parseAdts(toU8(bytes), off); },
        isAdts: isAdts,
        strip: strip,
        asc: asc,
        readAsc: readAsc,
        findDsi: findDsi,
        sanitizeDescription: sanitizeDescription,
        normalize: normalize,
        encoder: encoder,
        repairMp4: repairMp4,
        version: '1.1.0'
    };
})(typeof window !== 'undefined' ? window : (typeof self !== 'undefined' ? self : globalThis));
